import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Ban,
  BarChart3,
  Building2,
  CalendarClock,
  CircleCheck,
  ExternalLink,
  GraduationCap,
  KeyRound,
  Landmark,
  ListChecks,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  PlayCircle,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Wallet,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button, buttonClassName } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SecureImage } from '@/components/ui/SecureImage';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs, tabPanelId } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import {
  boardLabel,
  GENDER_TYPE_LABEL,
  MANAGEMENT_LABEL,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TONE,
  RESIDENTIAL_LABEL,
  instituteLevelLabel,
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
  type PaymentStatus,
} from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn, telHref } from '@/lib/utils';
import type { Institute } from '@/types/institute';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { institutesApi } from '../api/institutes.api';
import { EmailInstituteDialog } from '../components/EmailInstituteDialog';
import { InstituteLogoMark } from '../components/InstituteLogoMark';
import { InstituteAdminsPanel } from '../components/InstituteAdminsPanel';
import { SetAdminPasswordDialog } from '../components/SetAdminPasswordDialog';
import { useInstituteLogo } from '../hooks/useInstituteLogo';
import { instituteMapsUrl, isSubscriptionOverdue } from '../utils';
import { QueryError } from '@/components/ui/QueryError';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'admins', label: 'Administrators' },
  { value: 'danger', label: 'Danger zone' },
] as const;

type TabKey = (typeof TABS)[number]['value'];

export function InstituteDetailPage() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pageState = location.state as { justCreated?: boolean; justUpdated?: boolean } | null;

  const [tab, setTab] = useState<TabKey>('overview');
  // Support may read the whole record; only a SUPER_ADMIN changes it — the
  // edit, suspend, logo and every Danger zone endpoint refuse anyone else.
  const canManage = useIsSuperAdmin();
  const [dialog, setDialog] = useState<'suspend' | 'activate' | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  // Kept apart from `actionError`: a failed suspend or activate is read inside
  // its dialog, which covers the page alert while it is open.
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);

  const {
    data: institute,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => institutesApi.get(uuid), [uuid]);
  const logo = useInstituteLogo(uuid, Boolean(institute?.logoKey), setActionError);

  async function runAction(action: 'suspend' | 'activate') {
    if (action === 'suspend') {
      const trimmed = reason.trim();
      if (!trimmed) {
        setReasonError('Please provide a reason for suspension.');
        return;
      }
      if (trimmed.length < 5) {
        setReasonError('Reason must be at least 5 characters.');
        return;
      }
    }

    setIsActing(true);
    setDialogError(null);
    try {
      if (action === 'suspend') await institutesApi.suspend(uuid, reason.trim());
      else await institutesApi.activate(uuid);
      setDialog(null);
      setReason('');
      setReasonError(null);
      refetch();
    } catch (caught) {
      setDialogError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsActing(false);
    }
  }

  // A toast rather than the page alert: the Danger zone is read at the foot of
  // the tab, and the alert sits above the tab strip, out of sight.
  async function resendInvite(adminEmail: string) {
    setIsResendingInvite(true);
    try {
      await institutesApi.resendInvite(uuid);
      toast.success('Invite sent', `A new set-password link is on its way to ${adminEmail}.`);
    } catch (caught) {
      toast.error(
        'Could not resend the invite',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsResendingInvite(false);
    }
  }

  if (error) {
    return <QueryError title="Could not load this institute" error={error} onRetry={refetch} />;
  }

  if (isLoading || !institute) {
    return (
      <>
        <DeckPageHeader
          eyebrow="Customer"
          title="Loading institute…"
          description="Fetching this customer's record."
          backTo={{ to: '/platform/institutes', label: 'Back to institutes' }}
        />
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </>
    );
  }

  const isPending = institute.status === 'PENDING_SETUP';
  const dueDate = institute.subscriptionDueDate;
  const isOverdue = dueDate !== null && isSubscriptionOverdue(dueDate, institute.paymentStatus);
  const dueLabel =
    dueDate === null
      ? 'No due date set'
      : `${isOverdue ? 'Overdue since' : 'Due'} ${formatDate(dueDate)}`;
  const { tracked, enabled } = institute.features;
  const featuresLabel =
    tracked === 0
      ? 'No features yet'
      : `${tracked} ${tracked === 1 ? 'feature' : 'features'} · ${enabled} live`;

  return (
    <>
      <DeckPageHeader
        eyebrow="Customer"
        title={institute.displayName}
        description={institute.legalName}
        backTo={{ to: '/platform/institutes', label: 'Back to institutes' }}
        // The institute's own mark, so it is obvious whose record this is
        // before anybody has read the name above it.
        mark={
          <InstituteLogoMark
            canEdit={canManage}
            hasLogo={logo.hasLogo}
            displayName={institute.displayName}
            isUploading={logo.isUploading}
            onSelect={logo.upload}
            onRemove={logo.remove}
            onInvalid={setActionError}
          >
            {logo.hasLogo ? (
              <SecureImage
                path={institutesApi.logoPath(uuid)}
                alt={`${institute.displayName} logo`}
                reloadKey={logo.version}
                className="h-full w-full p-1"
                fallback={<Building2 className="h-5 w-5 text-accent" />}
              />
            ) : (
              <Building2 className="h-5 w-5 text-accent" aria-hidden />
            )}
          </InstituteLogoMark>
        }
        actions={
          <>
            {/* Call is a `tel:` link — only an anchor can hand off to the
                dialler. Email opens our own composer, as the card's does. Both
                reach the administrator, the person we deal with. */}
            {institute.adminContactPhone && (
              <a
                href={telHref(institute.adminContactPhone)}
                aria-label={`Call ${institute.displayName}'s administrator on ${institute.adminContactPhone}`}
                title={`Call ${institute.adminContactPhone}`}
                className={buttonClassName({ variant: 'secondary', iconOnly: true })}
              >
                <Phone className="h-4 w-4" aria-hidden />
              </a>
            )}
            <Button
              variant="secondary"
              iconOnly
              aria-label={`Email ${institute.displayName}'s administrator at ${institute.adminContactEmail}`}
              title={`Email ${institute.adminContactEmail}`}
              onClick={() => setIsEmailOpen(true)}
            >
              <Mail className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="secondary"
              iconOnly
              aria-label={`Report for ${institute.displayName}`}
              title="Report"
              onClick={() => navigate(`/platform/institutes/${uuid}/report`)}
            >
              <BarChart3 className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="secondary"
              leftIcon={<SlidersHorizontal className="h-4 w-4" />}
              onClick={() => navigate(`/platform/institutes/${uuid}/settings`)}
            >
              Settings
            </Button>
            {canManage && (
              <Button
                variant="secondary"
                leftIcon={<PencilLine className="h-4 w-4" />}
                // Tells the edit screen where its "back" goes — here, not the list.
                onClick={() =>
                  navigate(`/platform/institutes/${uuid}/edit`, {
                    state: { from: location.pathname },
                  })
                }
              >
                Edit
              </Button>
            )}
            {/* A new institute starts in Pending setup and nothing moves it on
                by itself, so it is offered Activate here — Suspend stays one
                tab away under Danger zone. A suspended one is offered the way
                back. */}
            {canManage &&
              (institute.status === 'SUSPENDED' || isPending ? (
                <Button
                  leftIcon={<PlayCircle className="h-4 w-4" />}
                  onClick={() => setDialog('activate')}
                >
                  {isPending ? 'Activate' : 'Reactivate'}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  leftIcon={<Ban className="h-4 w-4" />}
                  onClick={() => setDialog('suspend')}
                >
                  Suspend
                </Button>
              ))}
          </>
        }
      >
        {/* The three facts that identify the record, under the rule: what it is
            called is above, where it is and what state it is in are here. */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={SCHOOL_STATUS_TONE[institute.status]} withDot>
            {SCHOOL_STATUS_LABEL[institute.status]}
          </Badge>
          <span className="rounded-pill border border-line bg-surface px-3 py-1 font-mono text-xs text-ink-2 uppercase">
            {institute.code}
          </span>
          {/* Opens in Maps, in a new tab, as the customer card's place does. */}
          <a
            href={instituteMapsUrl(institute)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${institute.city}, ${institute.state} — open ${institute.displayName} in Google Maps (new tab)`}
            className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface px-3 py-1 text-xs text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
          >
            <MapPin className="h-3 w-3" aria-hidden />
            {institute.city}, {institute.state}
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
          {/* When the licence falls due, worded and coloured as the customer
              card says it — opens the payment history. */}
          <Link
            to={`/platform/institutes/${uuid}/subscription`}
            aria-label={`${dueLabel} — open ${institute.displayName}'s payment history`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill border bg-surface px-3 py-1 text-xs transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
              isOverdue ? 'border-crit text-crit' : 'border-line text-ink-2',
            )}
          >
            <CalendarClock className="h-3 w-3" aria-hidden />
            {dueLabel}
          </Link>
          {/* What they have bought: on their tracker, and switched on. */}
          <Link
            to={`/platform/features?institute=${uuid}`}
            aria-label={`${featuresLabel} — open ${institute.displayName}'s features`}
            className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface px-3 py-1 text-xs text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
          >
            <ListChecks className="h-3 w-3" aria-hidden />
            {featuresLabel}
          </Link>
        </div>
      </DeckPageHeader>

      {pageState?.justCreated && (
        <Alert tone="success" title="Institute created">
          A one-time link to set a password has been sent to {institute.adminContactEmail}. The
          institute stays in <strong>Pending setup</strong> until their administrator finishes
          onboarding.
        </Alert>
      )}

      {/* New institutes start with no features; until one is on, its people
          would sign in to an empty sidebar and Activate is refused. */}
      {enabled === 0 && (
        <Alert tone="warning" title="Add features for this institute">
          {institute.displayName} has no features switched on yet, so its people would see nothing
          and it cannot be activated. Add at least one on the Feature tracker.{' '}
          <Link
            to={`/platform/features?institute=${institute.uuid}`}
            className="font-[650] text-blue-600 underline underline-offset-2 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
          >
            Add features
          </Link>
        </Alert>
      )}

      {pageState?.justUpdated && (
        <Alert tone="success" title="Changes saved">
          {institute.displayName} has been updated.
        </Alert>
      )}

      {institute.status === 'SUSPENDED' && (
        <Alert tone="critical" title="This institute is suspended">
          Nobody at {institute.displayName} can sign in. Their data is untouched and returns the
          moment the institute is reactivated.
          {institute.suspensionReason && <> Reason: {institute.suspensionReason}</>}
        </Alert>
      )}

      {actionError && (
        <Alert tone="critical" title="Action failed">
          {actionError}
        </Alert>
      )}

      {/* ---------------- tabs ----------------
          The product's own tab strip. This screen had two `role="tab"` buttons
          with no roving focus, no `aria-controls` and no arrow-key movement,
          which announces itself as a tablist and then does not behave like one
          (CLAUDE.md §3). */}
      <Tabs
        // Every row under Danger zone is a SUPER_ADMIN write, so support is not
        // shown a tab of buttons the server would only refuse.
        options={canManage ? TABS : TABS.filter((option) => option.value !== 'danger')}
        value={tab} onChange={setTab} label="What to show about this institute" />

      {tab === 'overview' && (
        <div
          id={tabPanelId('overview')}
          role="tabpanel"
          aria-labelledby="overview-tab"
          className="flex flex-col gap-4"
        >
          <OverviewTab institute={institute} />
        </div>
      )}

      {tab === 'admins' && (
        <div
          id={tabPanelId('admins')}
          role="tabpanel"
          aria-labelledby="admins-tab"
          className="flex flex-col gap-4"
        >
          <InstituteAdminsPanel
            instituteUuid={uuid}
            instituteName={institute.displayName}
            canManage={canManage}
          />
        </div>
      )}

      {canManage && tab === 'danger' && (
        <div
          id={tabPanelId('danger')}
          role="tabpanel"
          aria-labelledby="danger-tab"
          className="flex flex-col gap-4"
        >
          <DangerRow
            icon={<Mail className="h-4 w-4" />}
            tone="caution"
            title="Resend the administrator invite"
            description={`Sends a fresh one-time password link to ${institute.adminContactEmail}. Any earlier link stops working.`}
            action={
              <Button
                variant="secondary"
                isLoading={isResendingInvite}
                onClick={() => resendInvite(institute.adminContactEmail)}
              >
                Resend invite
              </Button>
            }
          />
          <DangerRow
            icon={<KeyRound className="h-4 w-4" />}
            tone="caution"
            title="Set the administrator's password"
            description={`Replaces the password for ${institute.adminContactEmail} with one you choose. Use it only when the sign-in link cannot reach them — they are made to change it as soon as they sign in.`}
            action={
              <Button variant="secondary" onClick={() => setIsPasswordDialogOpen(true)}>
                Set password
              </Button>
            }
          />
          <DangerRow
            icon={<ShieldAlert className="h-4 w-4" />}
            tone="danger"
            title="Suspend this institute"
            description="Everyone is signed out and logins are refused until it is reactivated. Nothing is deleted."
            action={
              <Button variant="danger" onClick={() => setDialog('suspend')}>
                Suspend
              </Button>
            }
          />
          <DangerRow
            icon={<Trash2 className="h-4 w-4" />}
            tone="danger"
            title="Archive this institute"
            // Said in the row itself rather than in a tooltip: a disabled
            // button fires no hover in most browsers, so a tooltip on it is
            // never seen. There is no archive endpoint yet.
            description="For a customer who has left. The record is kept for audit and can be restored — it is never hard-deleted. Not available yet: suspend the institute in the meantime."
            action={
              <Button variant="danger" disabled title="Archiving is not available yet">
                Archive
              </Button>
            }
          />
        </div>
      )}

      {/* ---------------- dialogs ---------------- */}
      <ConfirmDialog
        isOpen={dialog === 'suspend'}
        title={`Suspend ${institute.displayName}?`}
        description="Every user at this institute is signed out immediately. Their data stays exactly as it is."
        confirmLabel="Suspend institute"
        tone="danger"
        isLoading={isActing}
        error={dialogError}
        onConfirm={() => runAction('suspend')}
        onCancel={() => {
          setDialog(null);
          setDialogError(null);
          setReason('');
          setReasonError(null);
        }}
      >
        <Textarea
          label="Reason"
          placeholder="Renewal unpaid since 30 June, third reminder sent."
          value={reason}
          error={reasonError ?? undefined}
          onChange={(event) => {
            setReason(event.target.value);
            if (reasonError) setReasonError(null);
          }}
          hint="Recorded in the audit log and shown to our support team."
        />
      </ConfirmDialog>

      <EmailInstituteDialog
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        institute={institute}
      />

      <SetAdminPasswordDialog
        instituteUuid={uuid}
        adminEmail={institute.adminContactEmail}
        isOpen={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
      />

      <ConfirmDialog
        isOpen={dialog === 'activate'}
        title={`${isPending ? 'Activate' : 'Reactivate'} ${institute.displayName}?`}
        description={
          isPending
            ? 'Marks setup as finished and the institute as a live customer. Nobody is signed out, and nothing they can do changes — Pending setup does not block sign-in.'
            : 'Logins start working again immediately for every user at this institute.'
        }
        confirmLabel={isPending ? 'Activate' : 'Reactivate'}
        isLoading={isActing}
        error={dialogError}
        onConfirm={() => runAction('activate')}
        onCancel={() => {
          setDialog(null);
          setDialogError(null);
        }}
      />
    </>
  );
}

// --- pieces -----------------------------------------------------------------

/**
 * Every field the create wizard asks for, optional ones included.
 *
 * A blank optional field is listed with a hyphen rather than left off:
 * the record is how we find out what the onboarding call still has to collect,
 * and a field that is simply absent cannot be noticed as missing.
 */
function OverviewTab({ institute }: { institute: Institute }) {
  const isCollege = institute.institutionType === 'COLLEGE';

  return (
    // What support reaches for first — is it paid, who to call, how to reach
    // them — sits at the top, and the registration paperwork below it.
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* What they pay us for the software. On the record as well as the
          customer list, because "have they paid?" is asked of one institute at
          least as often as it is scanned across all of them. */}
      <DetailCard title="Subscription" icon={<Wallet className="h-4 w-4" />}>
        <Row
          label="Licence fee"
          value={institute.subscriptionAmount && formatCurrency(institute.subscriptionAmount)}
        />
        <Row
          label="Payment"
          value={
            <Badge tone={PAYMENT_STATUS_TONE[institute.paymentStatus]} withDot>
              {PAYMENT_STATUS_LABEL[institute.paymentStatus]}
            </Badge>
          }
        />
        <Row
          label="Due date"
          value={
            institute.subscriptionDueDate && (
              <SubscriptionDue
                dueDate={institute.subscriptionDueDate}
                paymentStatus={institute.paymentStatus}
              />
            )
          }
        />
      </DetailCard>

      <DetailCard title="People" icon={<UserRound className="h-4 w-4" />}>
        <Row label="Principal" value={institute.principalName} />
        <Row label="Principal phone" value={institute.principalPhone} />
        <Row label="Principal email" value={institute.principalEmail} />
        <Row label="Administrator" value={institute.adminContactName} />
        <Row label="Designation" value={institute.adminContactDesignation} />
        <Row label="Admin email" value={institute.adminContactEmail} />
        <Row label="Admin phone" value={institute.adminContactPhone} />
        <Row label="Created" value={formatDate(institute.createdAt)} />
        {/* From the activation date rather than `onboardingStep`, which nothing
            records yet — a percentage from it would always read 0%. */}
        <Row
          label="Setup"
          value={
            institute.activatedAt ? (
              <span className="inline-flex items-center gap-1 text-accent">
                <CircleCheck className="h-3.5 w-3.5" /> Went live {formatDate(institute.activatedAt)}
              </span>
            ) : institute.status === 'PENDING_SETUP' ? (
              'Pending setup'
            ) : null
          }
        />
      </DetailCard>

      <DetailCard title="Contact" icon={<Phone className="h-4 w-4" />}>
        <Row label="Office phone" value={institute.phone} />
        <Row label="Alternate phone" value={institute.alternatePhone} />
        <Row label="WhatsApp" value={institute.whatsappNumber} />
        <Row label="Email" value={institute.email} />
        <Row label="Website" value={institute.website} />
      </DetailCard>

      <DetailCard title="Identity" icon={<Building2 className="h-4 w-4" />}>
        <Row label="Registered name" value={institute.legalName} />
        <Row label="Display name" value={institute.displayName} />
        <Row label="Short name" value={institute.shortName} />
        <Row label="Subdomain" value={institute.subdomain} />
        <Row label="Motto" value={institute.motto} />
        {/* Up here as well as under Subscription: it is the first thing support
            checks before ringing a customer, and this card is read first. */}
        <Row
          label="Subscription due"
          value={
            institute.subscriptionDueDate && (
              <SubscriptionDue
                dueDate={institute.subscriptionDueDate}
                paymentStatus={institute.paymentStatus}
              />
            )
          }
        />
      </DetailCard>

      {/* Line by line rather than one joined paragraph, so a blank second line
          or locality shows up as blank instead of quietly closing the gap. */}
      <DetailCard title="Address" icon={<MapPin className="h-4 w-4" />}>
        <Row label="Line 1" value={institute.addressLine1} />
        <Row label="Line 2" value={institute.addressLine2} />
        <Row label="Locality" value={institute.locality} />
        <Row label="City" value={institute.city} />
        <Row label="District" value={institute.district} />
        <Row label="State" value={institute.state} />
        <Row label="PIN code" value={institute.pincode} />
        <Row
          label="Map"
          value={
            <a
              href={instituteMapsUrl(institute)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-field text-accent transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
            >
              Open in Google Maps
              <ExternalLink className="h-3 w-3" aria-hidden />
              <span className="sr-only">(new tab)</span>
            </a>
          }
        />
      </DetailCard>

      <DetailCard title="Academic profile" icon={<GraduationCap className="h-4 w-4" />}>
        <Row label="Level" value={instituteLevelLabel(institute)} />
        <Row label="Classes" value={`${institute.lowestClass} – ${institute.highestClass}`} />
        <Row label="Intake" value={GENDER_TYPE_LABEL[institute.genderType]} />
        <Row label="Type" value={RESIDENTIAL_LABEL[institute.residentialType]} />
        <Row label="Medium" value={institute.mediumOfInstruction.join(', ')} />
        {/* A hyphen, not "None", when the list is empty: an institute never asked
            about streams has not said it has none. */}
        <Row label="Streams" value={institute.streams?.join(', ')} />
      </DetailCard>

      <DetailCard title="Recognition" icon={<ShieldCheck className="h-4 w-4" />}>
        {!isCollege && <Row label="Board" value={boardLabel(institute.board)} />}
        {!isCollege && institute.board === 'STATE' && (
          <Row label="State board" value={institute.stateBoardName} />
        )}
        <Row label="UDISE+" value={institute.udiseCode} />
        <Row label="Affiliation" value={institute.affiliationNumber} />
        <Row
          label="Valid till"
          value={institute.affiliationValidTill && formatDate(institute.affiliationValidTill)}
        />
        <Row label="Recognition no." value={institute.recognitionNumber} />
        <Row label="Established" value={institute.establishedYear} />
        <Row label="Management" value={MANAGEMENT_LABEL[institute.managementType]} />
      </DetailCard>

      <DetailCard title="Legal entity" icon={<Landmark className="h-4 w-4" />}>
        <Row label="Trust / society" value={institute.trustName} />
        <Row label="Trust reg. no." value={institute.trustRegistrationNo} />
        <Row label="PAN" value={institute.panNumber} />
        <Row label="GSTIN" value={institute.gstin} />
        <Row
          label="Signatory Aadhaar"
          value={institute.aadhaarLast4 && `•••• •••• ${institute.aadhaarLast4}`}
        />
      </DetailCard>
    </div>
  );
}

/**
 * The licence fee's due date, red once it is past and unpaid — matching the
 * customer card. Rendered only when a date is set, so `Row` can still show
 * its hyphen when there is none.
 */
function SubscriptionDue({
  dueDate,
  paymentStatus,
}: {
  dueDate: string;
  paymentStatus: PaymentStatus;
}) {
  const isOverdue = isSubscriptionOverdue(dueDate, paymentStatus);

  return (
    <span className={cn(isOverdue && 'text-crit')}>
      {isOverdue && 'Overdue since '}
      {formatDate(dueDate)}
    </span>
  );
}

/**
 * One panel of the record.
 *
 * The icon sits in a tinted chip and the whole card lifts on hover, which is
 * how every other panel in this console behaves — a record made of six flat
 * boxes reads as a printout rather than as part of the same product.
 */
function DetailCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card group p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent-line">
      <header className="mb-3 flex items-center gap-2.5 border-b border-line pb-2.5">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-field bg-accent-soft text-accent transition-transform duration-300 group-hover:scale-110"
          aria-hidden
        >
          {icon ?? <Building2 className="h-4 w-4" />}
        </span>
        <h2 className="lbl">{title}</h2>
      </header>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

/**
 * One labelled fact. A null or blank value shows a hyphen, never an empty gap —
 * quiet enough that the filled fields stand out, while screen readers still
 * hear "Not provided" rather than "hyphen".
 */
function Row({ label, value }: { label: string; value: ReactNode }) {
  const isEmpty = value === null || value === undefined || value === '';

  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-ink-3">{label}</span>
      {isEmpty ? (
        <span className="min-w-0 text-right text-ink-3">
          <span aria-hidden>-</span>
          <span className="sr-only">Not provided</span>
        </span>
      ) : (
        <span className="min-w-0 truncate text-right font-[560] text-ink">{value}</span>
      )}
    </div>
  );
}

/**
 * How much an action on this tab actually costs.
 *
 * `caution` reaches into somebody's account — a live sign-in link stops
 * working, a password is replaced under them. `danger` takes the institute
 * away from every one of its users at once.
 *
 * Graded rather than all red: four identical red edges teach the reader that
 * the colour means "this tab" and not "be careful", and by then it cannot warn
 * them about the two rows that genuinely need it.
 */
const ROW_TONE = {
  caution: { edge: 'border-l-warn', chip: 'bg-warn-bg text-warn' },
  danger: { edge: 'border-l-crit', chip: 'bg-crit-bg text-crit' },
} as const;

/**
 * One consequential action, with what it costs said before the button.
 *
 * Edged down the left rather than merely sitting under a tab called "Danger
 * zone": the tab is a label somebody has stopped reading by their second visit,
 * and the edge is on every row every time.
 */
function DangerRow({
  icon,
  title,
  description,
  action,
  tone,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
  tone: keyof typeof ROW_TONE;
}) {
  const palette = ROW_TONE[tone];

  return (
    <div
      className={cn(
        'card group flex flex-col gap-3 border-l-[3px] p-4 transition-all duration-300 hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between',
        palette.edge,
      )}
    >
      <div className="flex gap-3">
        <span
          className={cn(
            'mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-field transition-transform duration-300 group-hover:scale-110',
            palette.chip,
          )}
          aria-hidden
        >
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-[650] text-ink">{title}</p>
          <p className="mt-0.5 text-sm text-ink-2">{description}</p>
        </div>
      </div>
      <div className="shrink-0 sm:pl-4">{action}</div>
    </div>
  );
}
